import { useState } from "react";
import { MessageSquare, Save, CheckCircle2 } from "lucide-react";

export interface DiscussionQuestion {
  id: string;
  question: string;
  prompt?: string;
}

interface DiscussionQuestionsProps {
  questions: DiscussionQuestion[];
  title?: string;
  savedResponses?: Record<string, string>;
  onSave?: (responses: Record<string, string>) => void;
}

export function DiscussionQuestions({
  questions,
  title,
  savedResponses,
  onSave,
}: DiscussionQuestionsProps) {
  const [responses, setResponses] = useState<Record<string, string>>(savedResponses || {});
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onSave?.(responses);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const hasContent = Object.values(responses).some((v) => v.trim().length > 0);

  if (questions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-[var(--color-acrrm-dark)]" />
        <h3 className="font-heading text-lg font-semibold text-foreground">
          {title || "Discussion Questions"}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground">
        These questions are for your own reflection or to discuss with your team. There are no right or wrong answers. You can save your notes here and come back to them later.
      </p>

      {questions.map((q, i) => (
        <div key={q.id} className="rounded-xl border bg-card p-5">
          <p className="font-medium text-foreground mb-1">
            <span className="text-muted-foreground mr-2">{i + 1}.</span>
            {q.question}
          </p>
          {q.prompt && (
            <p className="text-sm text-muted-foreground mb-3 italic">{q.prompt}</p>
          )}
          <textarea
            value={responses[q.id] || ""}
            onChange={(e) => {
              setResponses((r) => ({ ...r, [q.id]: e.target.value }));
              setSaved(false);
            }}
            placeholder="Your thoughts..."
            rows={3}
            className="w-full rounded-lg border bg-background p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-acrrm-dark)]/20 focus:border-[var(--color-acrrm-dark)]"
          />
        </div>
      ))}

      {hasContent && (
        <button
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--color-acrrm-dark)] text-white text-sm font-medium py-2.5 px-6 hover:opacity-90 transition-opacity"
        >
          {saved ? (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Saved
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save notes
            </>
          )}
        </button>
      )}
    </div>
  );
}
